import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';

// The scene is a read-only projection of complete firmware reports. It smooths
// visual motion between reports, but never infers controller or plant state.
const MODEL_URL = new URL('./GLTF/Simulation_V4_2.glb', import.meta.url).href;
const Y_AXIS = new THREE.Vector3(0, 1, 0);
const Z_AXIS = new THREE.Vector3(0, 0, 1);
const LED = {
  red: new THREE.Color(0xff493f),
  amber: new THREE.Color(0xffc44f),
  green: new THREE.Color(0x4ae28c),
  off: new THREE.Color(0x555d5d),
  dark: new THREE.Color(0x000000),
};

const DEFAULT_STATE = {
  scenario: 'calm',
  phase: 'reset',
  windDirection: 0,
  windBand: 'calm',
  aligned: false,
  nacelleAngle: 0,
  targetAngle: 0,
  rotorBand: 'stopped',
  align: false,
  generatorEnable: false,
  active: false,
  runwayWindAlert: false,
  notice: 'none',
};

function createSkyGradientTexture() {
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 512;
  const context = canvas.getContext('2d');
  const gradient = context.createLinearGradient(0, 0, 0, canvas.height);
  gradient.addColorStop(0, '#8997b2');
  gradient.addColorStop(0.55, '#676764');
  gradient.addColorStop(1, '#6e665f');
  context.fillStyle = gradient;
  context.fillRect(0, 0, canvas.width, canvas.height);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

function createStreakTexture() {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 64;
  const context = canvas.getContext('2d');
  const image = context.createImageData(canvas.width, canvas.height);

  for (let y = 0; y < canvas.height; y += 1) {
    const verticalFade = Math.sin((y / (canvas.height - 1)) * Math.PI);
    for (let x = 0; x < canvas.width; x += 1) {
      const horizontalTaper = Math.sin((x / (canvas.width - 1)) * Math.PI) ** 1.5;
      const offset = (y * canvas.width + x) * 4;
      image.data[offset] = 255;
      image.data[offset + 1] = 255;
      image.data[offset + 2] = 255;
      image.data[offset + 3] = Math.round(horizontalTaper * verticalFade * 255);
    }
  }

  context.putImageData(image, 0, 0);
  return new THREE.CanvasTexture(canvas);
}

function shortestAngleDelta(from, to) {
  return Math.atan2(Math.sin(to - from), Math.cos(to - from));
}

function dampAngle(current, target, amount) {
  return current + shortestAngleDelta(current, target) * amount;
}

const WIND_SLEEVE_SEGMENT_DEGREES = Object.freeze({
  calm: -34,
  steady: -12,
  high: -1,
});

export function windSleeveSegmentAngle(windBand) {
  return THREE.MathUtils.degToRad(WIND_SLEEVE_SEGMENT_DEGREES[windBand]);
}

export function windFlowTransform(windAngle) {
  // windDirection names where the wind comes from. In scene coordinates the
  // resulting downwind vector is (-sin(angle), -cos(angle)). The GLB windsock
  // trails along local -Z, while each PlaneGeometry streak lies along local +X.
  return {
    x: -Math.sin(windAngle),
    z: -Math.cos(windAngle),
    sleeveYaw: windAngle,
    // The vane's broad tail is authored along +Z; its nose points along -Z.
    vaneYaw: windAngle + Math.PI,
    streakYaw: windAngle + Math.PI / 2,
  };
}

function materialFor(object) {
  if (!object?.material) return null;
  return Array.isArray(object.material) ? object.material[0] : object.material;
}

function setLed(object, enabled, color) {
  const material = materialFor(object);
  if (!material?.color) return;
  material.color.copy(enabled ? color : LED.off);
  if ('emissive' in material) {
    material.emissive.copy(enabled ? color : LED.dark);
    material.emissiveIntensity = enabled ? 1.8 : 0;
  }
  // Color/emissive are uniforms. needsUpdate would invalidate the material
  // program on every animation frame, although no shader feature has changed.
}

function rotateInstrument(object, speed, target, delta, response, axis) {
  // Speeds are radians/second, not radians/frame. Integrate the exponentially
  // damped speed so both acceleration and travel agree at 30, 60, and 120 fps.
  const blend = -Math.expm1(-response * delta);
  const angle = target * delta + (speed - target) * blend / response;
  if (object) object.rotateOnAxis(axis, angle);
  return speed + (target - speed) * blend;
}

function disposeObjects(root) {
  // GLTFLoader shares geometry, materials, and textures. Release each once;
  // ImageBitmaps also need closing because texture.dispose only frees GPU data.
  const geometries = new Set();
  const materials = new Set();
  const textures = new Set();
  const images = new Set();
  root.traverse((object) => {
    if (object.geometry) geometries.add(object.geometry);
    const items = Array.isArray(object.material) ? object.material : [object.material];
    items.filter(Boolean).forEach((material) => materials.add(material));
    object.shadow?.dispose();
  });
  materials.forEach((material) => {
    Object.values(material).filter((value) => value?.isTexture).forEach((texture) => textures.add(texture));
    material.dispose();
  });
  textures.forEach((texture) => {
    images.add(texture.source.data);
    texture.dispose();
  });
  images.forEach((image) => image?.close?.());
  geometries.forEach((geometry) => geometry.dispose());
}

export class AirportWindScene {
  constructor(host, { onReady = () => {}, debug = false } = {}) {
    this.host = host;
    this.onReady = onReady;
    this.debug = debug;
    this.state = { ...DEFAULT_STATE };
    this.reducedMotion = false;
    this.running = false;
    this.disposed = false;
    this.modelLoaded = false;
    this.nacelleAngle = 0;
    this.windAngle = 0;
    this.rotorSpeed = 0;
    this.anemometerSpeed = 0;

    this.scene = new THREE.Scene();
    this.skyTexture = createSkyGradientTexture();
    this.scene.background = this.skyTexture;
    this.scene.fog = new THREE.Fog(0xffe8b0, 90, 400);

    this.renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.host.appendChild(this.renderer.domElement);

    const pmremGenerator = new THREE.PMREMGenerator(this.renderer);
    const environmentScene = new THREE.Scene();
    environmentScene.background = this.skyTexture;
    this.environmentTarget = pmremGenerator.fromScene(environmentScene);
    this.scene.environment = this.environmentTarget.texture;
    pmremGenerator.dispose();

    this.camera = new THREE.PerspectiveCamera(35, 1, 0.5, 1000);
    this.camera.position.set(15, 4.4, 17);
    this.camera.lookAt(2, 4, 0);
    this.scene.add(this.camera);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.target.set(2, 4, 0);
    this.controls.enablePan = false;
    this.controls.enableZoom = debug;
    this.controls.enableRotate = debug;
    this.controls.minDistance = 9;
    this.controls.maxDistance = 34;
    this.controls.update();

    this.timer = new THREE.Timer();
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.host);
    this.resize();
    this.setupLights();
    this.setupWind();
    this.loadModel();

    this.animate = this.animate.bind(this);
  }

  setupLights() {
    const ambient = new THREE.HemisphereLight(0x8997b2, 0x6e665f, 0.7);
    this.scene.add(ambient);

    const sun = new THREE.DirectionalLight(0xfff3e0, 2.5);
    sun.position.set(45, 65, 40);
    sun.castShadow = true;
    sun.shadow.camera.near = 20;
    sun.shadow.camera.far = 160;
    sun.shadow.camera.top = 40;
    sun.shadow.camera.bottom = -18;
    sun.shadow.camera.left = -42;
    sun.shadow.camera.right = 42;
    sun.shadow.mapSize.set(2048, 2048);
    sun.shadow.bias = -0.0005;
    sun.shadow.normalBias = 0.04;
    this.scene.add(sun);
  }

  setupWind() {
    // Instancing keeps hundreds of animated streaks in one draw call.
    const geometry = new THREE.PlaneGeometry(1.4, 0.024);
    const streakTexture = createStreakTexture();
    const material = new THREE.MeshBasicMaterial({
      map: streakTexture,
      color: 0xf6fbfb,
      transparent: true,
      opacity: 0.5,
      side: THREE.DoubleSide,
      depthWrite: false,
    });
    this.windStripes = new THREE.InstancedMesh(geometry, material, 400);
    this.windStripes.frustumCulled = false;
    this.scene.add(this.windStripes);
    this.windDummy = new THREE.Object3D();
    const streakColor = new THREE.Color();
    const dimmestStreak = new THREE.Color(0xc8c8c8);
    this.windParticles = Array.from({ length: 400 }, (_, index) => {
      const length = 0.65 + Math.random() * 1.45;
      streakColor.set(0xffffff).lerp(dimmestStreak, Math.random() * 0.35);
      this.windStripes.setColorAt(index, streakColor);
      return {
        x: Math.random() * 34 - 17,
        y: Math.random() * 13 + 0.5,
        z: Math.random() * 34 - 17,
        speed: 0.55 + Math.random() * 0.85,
        length,
        halfLength: length * 0.7,
        width: 0.8 + Math.random() * 0.4,
      };
    });
    this.windStripes.instanceColor.needsUpdate = true;
  }

  loadModel() {
    new GLTFLoader().load(
      MODEL_URL,
      (gltf) => {
        const model = gltf.scene;
        // A navigation can finish while the download is still in flight.
        if (this.disposed) { disposeObjects(model); return; }
        const get = (name) => {
          const object = model.getObjectByName(name);
          if (!object) throw new Error(`Missing animated model part: ${name}`);
          return object;
        };
        this.scene.add(model);
        this.bladeShaft = get('Turbine_Shaft');
        this.nacelle = get('Turbine_Engine_House');
        this.anemometer = get('Anemometer');
        this.windVane = get('Wind_Vane');
        this.windSleeve = get('Wind_Sleeve_Structure');

        // Blender exports these moving parts as siblings. Reparenting preserves
        // world transforms while making each assembly animate as one object.
        this.attach(this.bladeShaft, [
          'Turbine_Blade_1_P2', 'Turbine_Blade_1_P3', 'Turbine_Blade_1_P4',
          'Turbine_Blade_2_P2', 'Turbine_Blade_2_P3', 'Turbine_Blade_2_P4',
          'Turbine_Blade_3_P2', 'Turbine_Blade_3_P3', 'Turbine_Blade_3_P4',
        ], model);
        this.attach(get('Wind_Sleeve_4'), ['Wind_Sleeve_5'], model);
        this.attach(get('Wind_Sleeve_3'), ['Wind_Sleeve_4'], model);
        this.attach(this.windSleeve, ['Wind_Sleeve_1', 'Wind_Sleeve_2', 'Wind_Sleeve_3'], model);
        this.attach(this.nacelle, [
          'Turbine_Engine_Rail', 'Turbine_Head_LED_1_Base', 'Turbine_Head_LED_Red',
          'Turbine_Head_LED_2_Base', 'Turbine_Head_LED_Yellow',
          'Turbine_Head_LED_3_Base', 'Turbine_Head_LED_Green', 'Turbine_Shaft',
        ], model);

        this.ledRed = get('Turbine_Head_LED_Red');
        this.ledAmber = get('Turbine_Head_LED_Yellow');
        this.ledGreen = get('Turbine_Head_LED_Green');
        this.turbineRoomLed = get('Turbine_Room_LED');
        this.buildingLed = get('Building_LED');
        [this.ledRed, this.ledAmber, this.ledGreen, this.turbineRoomLed, this.buildingLed]
          .filter(Boolean)
          .forEach((object) => {
            if (Array.isArray(object.material)) object.material = object.material.map((item) => item.clone());
            else if (object.material) object.material = object.material.clone();
          });

        model.traverse((child) => {
          if (!child.isMesh) return;
          child.castShadow = true;
          child.receiveShadow = true;
          const materials = Array.isArray(child.material) ? child.material : [child.material];
          materials.filter(Boolean).forEach((material) => {
            if (material.name === 'Background_01') material.fog = false;
          });
        });
        // Keep references out of the frame loop: these articulated segments
        // rotate relative to their parent, not relative to the scene origin.
        this.sleeveSegment3 = get('Wind_Sleeve_3');
        this.sleeveSegment4 = get('Wind_Sleeve_4');
        this.modelLoaded = true;
        this.onReady();
      },
      undefined,
      (error) => {
        if (this.disposed) return;
        console.error('[airport-wind] GLB load failed', error);
        this.onReady('The 3D scene could not be loaded. Reload the page to try again.');
      },
    );
  }

  attach(parent, childNames, root) {
    if (!parent) throw new Error('Missing animated assembly');
    childNames.forEach((name) => {
      const child = root.getObjectByName(name);
      if (!child) throw new Error(`Missing animated model part: ${name}`);
      if (child !== parent) parent.attach(child);
    });
  }

  applyState(state) {
    // Reports are complete snapshots; replacing the object prevents stale fields.
    this.state = { ...state };
  }

  setReducedMotion(reduced) {
    this.reducedMotion = reduced;
  }

  start() {
    if (this.running || this.disposed) return;
    this.running = true;
    this.timer.reset();
    this.renderer.setAnimationLoop(this.animate);
  }

  dispose() {
    if (this.disposed) return;
    this.disposed = true;
    this.running = false;
    this.renderer.setAnimationLoop(null);
    this.resizeObserver.disconnect();
    this.controls.dispose();
    disposeObjects(this.scene);
    this.environmentTarget.dispose();
    this.skyTexture.dispose();
    this.timer.dispose();
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }

  resize() {
    const width = Math.max(1, this.host.clientWidth);
    const height = Math.max(1, this.host.clientHeight);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
  }

  animate() {
    if (!this.running) return;
    this.timer.update();
    const delta = Math.min(this.timer.getDelta(), 0.05);
    this.updateScene(delta);
    this.controls.update();
    this.renderer.render(this.scene, this.camera);
  }

  updateScene(delta) {
    const snap = this.reducedMotion ? 1 : -Math.expm1(-delta * 2.8);
    const targetNacelle = THREE.MathUtils.degToRad(this.state.nacelleAngle);
    const targetWind = THREE.MathUtils.degToRad(this.state.windDirection);
    // Use the shortest circular path so bearings crossing 0/360 do not spin around.
    this.nacelleAngle = dampAngle(this.nacelleAngle, targetNacelle, snap);
    this.windAngle = dampAngle(this.windAngle, targetWind, snap);
    const flow = windFlowTransform(this.windAngle);

    if (this.nacelle) this.nacelle.rotation.y = this.nacelleAngle;
    // Set the full vane rotation: its exported 180-degree yaw decomposes into
    // Euler X/Z flips. Editing only Y relies on those hidden imported flips.
    if (this.windVane) this.windVane.rotation.set(0, flow.vaneYaw, 0);
    if (this.windSleeve) this.windSleeve.rotation.y = flow.sleeveYaw;

    const windStrength = { calm: 0, steady: 0.58, high: 1 }[this.state.windBand];
    // The segments are nested, so their angles combine toward the tip. Negative
    // rotation lets gravity droop the sleeve; zero is horizontal in this model.
    const sleeveAngle = windSleeveSegmentAngle(this.state.windBand);
    if (this.sleeveSegment3) this.sleeveSegment3.rotation.x += (sleeveAngle - this.sleeveSegment3.rotation.x) * snap;
    if (this.sleeveSegment4) this.sleeveSegment4.rotation.x += (sleeveAngle - this.sleeveSegment4.rotation.x) * snap;

    const anemometerTarget = { calm: 0, steady: -2.1, high: -4.5 }[this.state.windBand];
    this.anemometerSpeed = rotateInstrument(this.reducedMotion ? null : this.anemometer,
      this.anemometerSpeed, anemometerTarget, delta, 3.5, Y_AXIS);

    // Bands select a visual speed, not a new plant calculation. Once firmware
    // reports stopped, stop exactly; do not keep coasting after the safety stop.
    const rotorTarget = { stopped: 0, accelerating: 1.5, running: 1.5, decelerating: 0 }[this.state.rotorBand];
    if (this.state.rotorBand === 'stopped') this.rotorSpeed = 0;
    this.rotorSpeed = rotateInstrument(this.reducedMotion ? null : this.bladeShaft,
      this.rotorSpeed, rotorTarget, delta, 4, Z_AXIS);

    setLed(this.ledRed, this.state.runwayWindAlert || this.state.notice !== 'none', LED.red);
    setLed(this.ledAmber, this.state.align, LED.amber);
    setLed(this.ledGreen, this.state.generatorEnable, LED.green);
    setLed(this.turbineRoomLed, this.state.active, LED.amber);
    setLed(this.buildingLed, this.state.runwayWindAlert, LED.red);
    this.updateWind(delta, windStrength, flow);
  }

  updateWind(delta, strength, flow) {
    if (this.reducedMotion || strength === 0) {
      this.windStripes.count = 0;
      return;
    }

    const visible = Math.floor(42 + strength * 358);
    this.windStripes.count = visible;

    for (let index = 0; index < visible; index += 1) {
      const particle = this.windParticles[index];
      const movement = particle.speed * (1.5 + strength * 5) * delta;
      particle.x += flow.x * movement;
      particle.z += flow.z * movement;
      // Wrap the finite particle field around the camera instead of reallocating it.
      if (particle.x - particle.halfLength > 17) particle.x -= 34;
      else if (particle.x + particle.halfLength < -17) particle.x += 34;
      if (particle.z - particle.halfLength > 17) particle.z -= 34;
      else if (particle.z + particle.halfLength < -17) particle.z += 34;

      this.windDummy.position.set(particle.x, particle.y, particle.z);
      this.windDummy.rotation.set(0, flow.streakYaw, 0);
      this.windDummy.scale.set(particle.length, particle.width, 1);
      this.windDummy.updateMatrix();
      this.windStripes.setMatrixAt(index, this.windDummy.matrix);
    }
    this.windStripes.instanceMatrix.needsUpdate = true;
  }
}
